import * as React from "react";

import { TableColumn, TableColumnType } from "../../../components/datatables/columns/DefaultColumn/DefaultColumn";
import { IconText } from "../../../components/synced/ui/IconText/IconText";
import { Pill } from "../../../components/synced/ui/Pill/Pill";
import { ColumnHeaderLabel, ColumnHeaderLabelSortMeta } from "../../../components/synced/ui/ColumnHeaderLabel/ColumnHeaderLabel";
import { ColumnDropdownFilter } from "../../../components/synced/ui/ColumnDropdownFilter/ColumnDropdownFilter";
import { ColumnDateRangeFilter } from "../../../components/synced/ui/ColumnDateRangeFilter/ColumnDateRangeFilter";
import { toCreatedQueryParams, fromCreatedQueryParams } from "../helpers/createdDateRangeToQueryParams";
import { getProjectDocStatusPillStyle } from "../../../helpers/mapping/ProjectDocStatusPillStyle";
import { getProjectDocTypePillStyle } from "../../../helpers/mapping/ProjectDocTypePillStyle";
import { projectDocStatusFilterOptions } from "../../../helpers/mapping/ProjectDocStatusFilterOptions";
import { projectDocTypeFilterOptions } from "../../../helpers/mapping/ProjectDocTypeFilterOptions";
import { resolveFieldData } from "../../../helpers/utils/FieldDataResolver";
import { projectDocTypeAbbreviations } from "../../../helpers/mapping/ProjectDocTypeAbbreviations";

const headerFor = (label: string, field: string, sortable = true) =>
  (options: { props?: { multiSortMeta?: ColumnHeaderLabelSortMeta[] } }) =>
    <ColumnHeaderLabel label={label} sortable={sortable} field={field} multiSortMeta={options?.props?.multiSortMeta} />;

export const projectDocColumns: Array<TableColumn> = [
  {
    field: `Title`, header: headerFor(`Документация`, `Title`), filterField: `TitleContains`,
    showFilterMenu: false,
    style: { maxWidth: `300px`, minWidth: `140px` },
    url: { getLinkURL: (id) => `/projectdocs/${id}`, field: `Id` },
    body: (row) => <IconText icon="fa-regular fa-file-lines" text={row.Title} className="row-primary" />,
  },
  {
    field: `Project.Title`, header: headerFor(`Название проекта`, `Project.Title`), filterField: `ProjectTitle`,
    showFilterMenu: false,
    style: { maxWidth: `240px`, minWidth: `120px` },
    body: (row) => <IconText icon="fa-regular fa-building" text={resolveFieldData(row, `Project.Title`)} className="row-secondary" />,
  },
  {
    field: `DocType.Title`, header: headerFor(`Вид документа`, `DocType.Title`), filterField: `DocTypeId`,
    showFilterMenu: false,
    filterElementFactory: (dtRef, filters) => (
      <ColumnDropdownFilter
        dtRef={dtRef} filters={filters} field="DocTypeId" options={projectDocTypeFilterOptions}
        getValueLabel={(o) => projectDocTypeAbbreviations[o.value] ?? o.label}
        toQueryValue={(v: any) => (v ? v : undefined)} matchMode="equals" />
    ),
    style: { maxWidth: `120px`, minWidth: `80px` },
    body: (row) => {
      const s = getProjectDocTypePillStyle(row.DocTypeId);
      const abbr = projectDocTypeAbbreviations[row.DocTypeId];
      const fullTitle = resolveFieldData(row, `DocType.Title`);
      return (
        <span className="doctype-pill-tooltip-target" data-pr-tooltip={fullTitle}>
          <Pill icon="fa-regular fa-folder-open" label={abbr ?? fullTitle} bg={s.bg} color={s.color} />
        </span>
      );
    },
  },
  {
    field: `CWContractorData`, header: headerFor(`Контрагент`, `CWContractorData`, false), filter: false, sortable: false,
    style: { maxWidth: `260px`, minWidth: `130px` },
    body: (row) => <IconText icon="fa-solid fa-briefcase" text={row.CWContractorData} className="row-secondary" />,
  },
  {
    field: `Status.Title`, header: headerFor(`Статус`, `Status.Title`), filterField: `StatusIds`,
    showFilterMenu: false,
    filterElementFactory: (dtRef, filters) => (
      <ColumnDropdownFilter
        dtRef={dtRef} filters={filters} field="StatusIds" options={projectDocStatusFilterOptions}
        toQueryValue={(v: any) => (v ? [v] : [])} matchMode="in" />
    ),
    style: { maxWidth: `190px`, minWidth: `100px` },
    body: (row) => {
      const s = getProjectDocStatusPillStyle(row.StatusId);
      return <Pill label={resolveFieldData(row, `Status.Title`)} bg={s.bg} color={s.color} dotColor={s.dot} />;
    },
  },
  {
    field: `Created`, header: headerFor(`Дата`, `Created`), type: TableColumnType.date, filterField: `CreatedQueryParams`,
    style: { maxWidth: `130px`, minWidth: `90px` },
    showFilterMenu: false,
    filterElementFactory: (dtRef, filters) => (
      <ColumnDateRangeFilter
        dtRef={dtRef} filters={filters} field="CreatedQueryParams"
        toQueryValue={toCreatedQueryParams}
        fromQueryValue={fromCreatedQueryParams} />
    ),
     bodyClassName: `row-tertiary`,
  },
];
